import { supabase } from "./supabase";

export async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const callerHeaders = (options.headers as Record<string, string>) ?? {};

  // Only default to JSON if the caller didn't supply a Content-Type.
  // Passing headers:{} (empty object) opts out, letting the browser set
  // the correct Content-Type (e.g. multipart boundary for FormData).
  const hasCallerContentType = "Content-Type" in callerHeaders || "content-type" in callerHeaders;
  const callerPassedHeaders = options.headers !== undefined;

  const headers: Record<string, string> = {
    ...(callerPassedHeaders && !hasCallerContentType ? {} : { "Content-Type": "application/json" }),
    ...callerHeaders,
  };

  if (session?.access_token) {
    headers["Authorization"] = `Bearer ${session.access_token}`;
  }

  return fetch(url, { ...options, headers });
}

/**
 * Thrown by authFetchOrThrow on a non-2xx response. Carries the status and
 * the parsed `detail` (the backend's HTTPException detail string) so callers
 * can surface a real message instead of a generic failure.
 */
export class AuthFetchError extends Error {
  constructor(
    public readonly status: number,
    public readonly url: string,
    public readonly detail?: string,
  ) {
    super(`Request to ${url} failed: ${status}${detail ? ` -- ${detail}` : ""}`);
    this.name = "AuthFetchError";
  }
}

/**
 * authFetch for CRITICAL paths that must NOT fail silently.
 *
 * Plain authFetch returns the raw Response, so a non-2xx is invisible
 * unless the caller checks res.ok -- and the codebase is full of
 * `.then(r => r.json()).catch(() => {})` that swallow it (this hid the
 * walkthrough 422). Use authFetchOrThrow for writes whose failure the user
 * needs to know about (walkthrough step stamps, plan-gating reads): it
 * throws AuthFetchError on any non-2xx, with the backend detail attached.
 */
export async function authFetchOrThrow(
  url: string,
  options: RequestInit = {},
): Promise<Response> {
  const res = await authFetch(url, options);
  if (!res.ok) {
    let detail: string | undefined;
    try {
      const body = await res.clone().json();
      if (body && typeof body.detail === "string") detail = body.detail;
    } catch {
      // Non-JSON error body; the status alone is enough to surface.
    }
    throw new AuthFetchError(res.status, url, detail);
  }
  return res;
}
